import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ordenesApi, controlHHApi, comprasApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { StatusBadge } from '../components/StatusBadge';
import { Skeleton } from '../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Pencil, Trash2, Calendar, User, Building2, FileText, DollarSign, Clock, Users, Printer } from 'lucide-react';

export const OrdenTrabajoDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canVerMontos = (
    ['admin', 'gerencia', 'contabilidad'].includes(user?.rol) ||
    user?.permisos?.informacion?.acciones?.ots_ver_montos === true ||
    user?.permisos?.informacion?.editar === true
  );
  const [ot, setOt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [resumenHH, setResumenHH] = useState(null);
  const [detalleHH, setDetalleHH] = useState([]);
  const [loadingHH, setLoadingHH] = useState(false);
  const [costoReal, setCostoReal] = useState(null);

  useEffect(() => {
    loadOt();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadOt = async () => {
    try {
      const res = await ordenesApi.getOne(id);
      setOt(res.data);
      // Load HH data after getting the OT
      if (res.data?.numero_ot) {
        loadHHData(res.data.numero_ot);
      }
    } catch (error) {

      toast({ title: 'Error al cargar la orden de trabajo', variant: 'destructive' });
      navigate('/ordenes-trabajo');
    } finally {
      setLoading(false);
    }
  };

  const loadHHData = async (otNumero) => {
    setLoadingHH(true);
    try {
      const [resumenRes, detalleRes, costoRes] = await Promise.all([
        controlHHApi.getResumenOT(otNumero),
        controlHHApi.getDetalleOT(otNumero),
        comprasApi.getCostoMaterialesOT(otNumero).catch(() => null)
      ]);
      setResumenHH(resumenRes.data);
      setDetalleHH(detalleRes.data || []);
      setCostoReal(costoRes?.data || null);
    } catch (error) { console.error('caught:', error); } finally {
      setLoadingHH(false);
    }
  };

  const handleDelete = async () => {
    try {
      await ordenesApi.delete(id);
      toast({ title: 'Orden de trabajo eliminada' });
      navigate('/ordenes-trabajo');
    } catch (_error) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL');
  };

  if (loading) {
    return (<div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-[400px]" /></div>);
  }

  if (!ot) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orden-trabajo-detail">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ordenes-trabajo')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold tracking-tight">OT-{ot.numero_ot}</h1>
              <StatusBadge status={ot.estado} />
            </div>
            <p className="text-muted-foreground">{ot.descripcion}</p>
          </div>
        </div>
        <div className="flex gap-2 w-full sm:w-auto print:hidden">
          <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
            <Printer className="w-4 h-4 mr-2" />Imprimir
          </Button>
          <Button variant="outline" className="flex-1 sm:flex-none" asChild data-testid="edit-btn">
            <Link to={`/ordenes-trabajo/${id}/editar`}><Pencil className="w-4 h-4 mr-2" />Editar</Link>
          </Button>
          <Button variant="destructive" className="flex-1 sm:flex-none" onClick={() => setShowDelete(true)} data-testid="delete-btn">
            <Trash2 className="w-4 h-4 mr-2" />Eliminar
          </Button>
        </div>
      </div>

      {showDelete && (
        <Card className="border-2 border-destructive/50 bg-destructive/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="font-medium">¿Eliminar OT-{ot.numero_ot}?</p><p className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</p></div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowDelete(false)}>Cancelar</Button>
                <Button variant="destructive" size="sm" onClick={handleDelete}>Eliminar</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Navigation - Native buttons */}
      <div className="flex gap-2 border-b pb-2">
        <button
          onClick={() => setActiveTab('general')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeTab === 'general' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
        >
          Información General
        </button>
        {canVerMontos && (
          <button
            onClick={() => setActiveTab('presupuesto')}
            className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeTab === 'presupuesto' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
          >
            Presupuesto
          </button>
        )}
        <button
          onClick={() => setActiveTab('horas')}
          className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors ${activeTab === 'horas' ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Horas Hombre
          {resumenHH?.total_horas > 0 && (
            <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
              {resumenHH.total_horas.toLocaleString('es-CL', { maximumFractionDigits: 1 })}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="font-heading flex items-center gap-2"><FileText className="w-5 h-5" />Datos del Trabajo</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-sm text-muted-foreground">Proyecto</p><p className="font-mono font-medium">{ot.proyecto_codigo}</p></div>
                  <div><p className="text-sm text-muted-foreground">Área</p><p className="font-medium">{ot.area}</p></div>
                </div>
                <Separator />
                <div><p className="text-sm text-muted-foreground">Descripción</p><p className="font-medium">{ot.descripcion}</p></div>
                {ot.observacion && (<><Separator /><div><p className="text-sm text-muted-foreground">Observaciones</p><p className="text-sm">{ot.observacion}</p></div></>)}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Building2 className="w-5 h-5" />Cliente</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><p className="text-sm text-muted-foreground">Razón Social</p><p className="font-medium">{ot.cliente_nombre}</p></div>
                {ot.contacto_cliente && (<div><p className="text-sm text-muted-foreground">Contacto</p><p className="font-medium">{ot.contacto_cliente}</p></div>)}
                {ot.responsable && (<div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" /><div><p className="text-sm text-muted-foreground">Responsable</p><p className="font-medium">{ot.responsable}</p></div></div>)}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Calendar className="w-5 h-5" />Fechas y Datos Comerciales</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                <div><p className="text-sm text-muted-foreground">Fecha Inicio</p><p className="font-medium">{formatDate(ot.fecha_inicio)}</p></div>
                <div><p className="text-sm text-muted-foreground">Fecha Término</p><p className="font-medium">{formatDate(ot.fecha_termino)}</p></div>
                <div><p className="text-sm text-muted-foreground">N° Cotización</p><p className="font-mono font-medium">{ot.cotizacion || '-'}</p></div>
                <div><p className="text-sm text-muted-foreground">Orden Compra</p><p className="font-mono font-medium">{ot.orden_compra || '-'}</p></div>
                {canVerMontos && (
                  <div><p className="text-sm text-muted-foreground">Monto</p><p className="font-mono font-medium">{formatCurrency(ot.monto)}</p></div>
                )}
                <div><p className="text-sm text-muted-foreground">Garantía</p><p className="font-medium">{ot.garantia ? 'Sí' : 'No'}</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'presupuesto' && canVerMontos && (
        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><DollarSign className="w-5 h-5" />Presupuesto de la Orden</CardTitle><CardDescription>Desglose de costos presupuestados</CardDescription></CardHeader>
          <CardContent>
            {(() => {
              const pres = ot.presupuesto || {};
              const valorHH = pres.valor_hh || 12500;
              const presCosto = (pres.materiales || 0) + (pres.servicios_terceros || 0) + ((pres.numero_hh || 0) * valorHH) + (pres.gastos_generales || 0);
              const realManoObra = (resumenHH?.total_horas || 0) * valorHH;
              const realCosto = (costoReal?.total_materiales || 0) + (costoReal?.total_sstt || 0) + realManoObra + (costoReal?.total_gastos_generales || 0);
              const desv = realCosto - presCosto;
              const pct = presCosto > 0 ? Math.round((realCosto / presCosto) * 100) : null;
              const over = presCosto > 0 && realCosto > presCosto;
              const warn = !over && pct !== null && pct >= 90;
              if (presCosto <= 0) {
                return (
                  <div data-testid="presupuesto-vs-real-line" className="mb-6 p-4 rounded-lg border bg-muted text-sm text-muted-foreground">
                    Sin presupuesto cargado — Gasto real a la fecha: <span className="font-mono font-bold text-foreground">{formatCurrency(realCosto)}</span>
                  </div>
                );
              }
              return (
                <div data-testid="presupuesto-vs-real-line" className={`mb-6 p-4 rounded-lg border ${over ? 'bg-red-50 border-red-300' : warn ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-300'}`}>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                    <span className="font-medium">Presupuesto vs Real:</span>
                    <span>Gasto Real <span className="font-mono font-bold">{formatCurrency(realCosto)}</span></span>
                    <span className="text-muted-foreground">de</span>
                    <span>Presupuestado <span className="font-mono font-bold">{formatCurrency(presCosto)}</span></span>
                    <span data-testid="presupuesto-desviacion" className={`font-mono font-bold ${over ? 'text-red-700' : warn ? 'text-amber-700' : 'text-emerald-700'}`}>
                      {over ? `▲ Sobregiro +${formatCurrency(desv)}` : `▼ Disponible ${formatCurrency(-desv)}`} ({pct}% consumido)
                    </span>
                  </div>
                  <div className="mt-2 h-2 w-full rounded-full bg-white/70 overflow-hidden border">
                    <div className={`h-full rounded-full ${over ? 'bg-red-500' : warn ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Prespto. Materiales</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.materiales)}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Prespto. SS.TT.</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.servicios_terceros)}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Valor HH</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.valor_hh)}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">N° H.H.</p><p className="font-mono text-xl font-bold">{ot.presupuesto?.numero_hh || 0}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Prespto. Mano Obra</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.mano_obra)}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Gastos Generales</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.gastos_generales)}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Utilidad</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.utilidad)}</p></div>
              <div className="p-4 rounded-lg bg-muted"><p className="text-sm text-muted-foreground">Kilos</p><p className="font-mono text-xl font-bold">{ot.presupuesto?.kilos || 0}</p></div>
              <div className="p-4 rounded-lg bg-primary text-primary-foreground col-span-2"><p className="text-sm opacity-80">Total Presupuesto</p><p className="font-mono text-xl font-bold">{formatCurrency(ot.presupuesto?.total)}</p></div>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'horas' && (
        <div className="space-y-6">
          {/* Resumen HH */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Resumen de Horas Hombre
              </CardTitle>
              <CardDescription>Total de horas trabajadas en esta OT</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingHH ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
              ) : resumenHH ? (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Total Horas</p>
                    <p className="font-mono text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {resumenHH.total_horas?.toLocaleString('es-CL', { maximumFractionDigits: 1 }) || 0}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Horas Normales</p>
                    <p className="font-mono text-xl font-bold">
                      {resumenHH.total_horas_normales?.toLocaleString('es-CL', { maximumFractionDigits: 1 }) || 0}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Horas Extra</p>
                    <p className="font-mono text-xl font-bold">
                      {resumenHH.total_horas_extra?.toLocaleString('es-CL', { maximumFractionDigits: 1 }) || 0}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Registros</p>
                    <p className="font-mono text-xl font-bold">
                      {resumenHH.cantidad_registros?.toLocaleString() || 0}
                    </p>
                  </div>
                  <div className="p-4 rounded-lg bg-muted">
                    <p className="text-sm text-muted-foreground">Trabajadores</p>
                    <p className="font-mono text-xl font-bold">
                      {resumenHH.cantidad_trabajadores || 0}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">No hay datos de horas para esta OT</p>
              )}
            </CardContent>
          </Card>

          {/* Detalle por trabajador */}
          {detalleHH.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Detalle por Trabajador
                </CardTitle>
                <CardDescription>Horas trabajadas por cada persona</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>N°</TableHead>
                        <TableHead>Trabajador</TableHead>
                        <TableHead className="text-right">Días</TableHead>
                        <TableHead className="text-right">HH Normales</TableHead>
                        <TableHead className="text-right">HH Extra</TableHead>
                        <TableHead className="text-right">Total HH</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detalleHH.map((trab, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono">{trab._id}</TableCell>
                          <TableCell className="font-medium">{trab.trabajador_nombre || '-'}</TableCell>
                          <TableCell className="text-right">{trab.dias_trabajados}</TableCell>
                          <TableCell className="text-right font-mono">
                            {trab.total_horas_normales?.toLocaleString('es-CL', { maximumFractionDigits: 1 })}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {trab.total_horas_extra?.toLocaleString('es-CL', { maximumFractionDigits: 1 })}
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            {trab.total_horas?.toLocaleString('es-CL', { maximumFractionDigits: 1 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};
